import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { daysUntil } from '@/utils/daysUntil';
import { SESSION_KEYS } from '@/config/constants';
import { metaAdsApi } from '@/services/api/metaAds';
import { useAuthStore } from '@/store/authStore';
import { debug } from '@/utils/debug';

/**
 * Per-account warning banner that surfaces when a Meta access token is
 * within the backend's 7-day reprompt window (TOKEN_REPROMPT_WINDOW in
 * src/api/services/meta_oauth_service_db.py:72). Reads expiry from
 * `authStore.linkedMetaAccountDetails` — populated by
 * `preloadLinkedAccounts` on login, so no Home-mount fetch.
 *
 * Mirrors `PolicyWarningBanner` (Tailwind + cn, 24h localStorage
 * dismissal). Plan §4.2 explicitly calls out "do not skip this — users
 * will silently lose their connection otherwise" — Meta has no silent
 * refresh, so the reconnect prompt is the only safety net.
 */

interface MetaTokenExpiringBannerProps {
  className?: string;
}

export const DISMISS_STORAGE_KEY_PREFIX = 'meta-token-banner-dismissed:';
const DISMISS_DURATION_HOURS = 24;

function getDismissStorageKey(memberUuid: string | null): string | null {
  if (!memberUuid) return null;
  return `${DISMISS_STORAGE_KEY_PREFIX}${memberUuid}`;
}

function getDismissedAccounts(memberUuid: string | null): Map<string, number> {
  const key = getDismissStorageKey(memberUuid);
  if (!key) return new Map();
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return new Map();
    const parsed = JSON.parse(stored) as [string, number][];
    const now = Date.now();
    return new Map(parsed.filter(([, expiry]) => expiry > now));
  } catch {
    return new Map();
  }
}

function saveDismissedAccounts(
  memberUuid: string | null,
  dismissed: Map<string, number>,
) {
  const key = getDismissStorageKey(memberUuid);
  if (!key) return;
  localStorage.setItem(key, JSON.stringify([...dismissed]));
}

export function MetaTokenExpiringBanner({
  className,
}: MetaTokenExpiringBannerProps) {
  const memberUuid = useAuthStore((s) => s.memberUuid);
  const linkedMetaAccountDetails = useAuthStore(
    (s) => s.linkedMetaAccountDetails,
  );
  // memberUuid can be null on first mount during session restore; once
  // checkExistingAuth populates it we re-read the per-member dismissed
  // map so the banner doesn't read/write under the wrong (or null) key.
  // Initializing to an empty Map and syncing in useEffect (vs eagerly
  // reading in useState's initializer) is intentional — a useState init
  // function runs only once with the first-mount value of memberUuid.
  const [dismissed, setDismissed] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    setDismissed(getDismissedAccounts(memberUuid));
  }, [memberUuid]);
  const [reconnectingId, setReconnectingId] = useState<string | null>(null);

  const now = Date.now();
  const expiringAccounts = linkedMetaAccountDetails.filter((acc) => {
    if (!acc.tokenExpiringSoon) return false;
    const expiry = dismissed.get(acc.id);
    return !expiry || expiry <= now;
  });

  if (expiringAccounts.length === 0) {
    return null;
  }

  const handleDismiss = (accountId: string) => {
    const expiry = Date.now() + DISMISS_DURATION_HOURS * 60 * 60 * 1000;
    setDismissed((prev) => {
      const updated = new Map(prev);
      updated.set(accountId, expiry);
      saveDismissedAccounts(memberUuid, updated);
      return updated;
    });
  };

  const handleReconnect = async (accountId: string) => {
    if (reconnectingId) return;
    setReconnectingId(accountId);
    try {
      const { authorization_url, state } =
        await metaAdsApi.getAuthorizationUrl();
      // Stash state for the callback handler at /oauth/callback/meta.
      // Same flow as the initial-link button in Home/index.tsx:203-220.
      window.sessionStorage.setItem(SESSION_KEYS.META_OAUTH_STATE, state);
      window.location.assign(authorization_url);
    } catch (err) {
      debug.error('MetaTokenExpiringBanner: Failed to start reconnect flow', err);
      setReconnectingId(null);
    }
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {expiringAccounts.map((acc) => {
        const days =
          acc.tokenExpiresAt !== null ? daysUntil(acc.tokenExpiresAt) : 0;
        const displayName = acc.businessName || acc.id;
        const isReconnecting = reconnectingId === acc.id;
        return (
          <div
            key={acc.id}
            role="alert"
            className="flex items-start gap-3 px-4 py-3 rounded-lg border bg-orange-50 border-orange-200 text-orange-800"
          >
            <svg
              className="h-5 w-5 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                Reconnect {displayName}
              </p>
              <p className="text-xs mt-0.5">
                {days <= 0
                  ? 'Your Meta access token has expired. Reconnect to keep data flowing.'
                  : days === 1
                    ? 'Your Meta access token expires in 1 day. Reconnect to keep data flowing.'
                    : `Your Meta access token expires in ${days} days. Reconnect to keep data flowing.`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => handleReconnect(acc.id)}
                // Disable every reconnect button as soon as any is in
                // flight — `handleReconnect` already early-returns on a
                // re-entry guard, so leaving sibling buttons enabled
                // would render them as clickable-no-ops.
                disabled={Boolean(reconnectingId)}
                aria-label={`Reconnect Meta account ${displayName}`}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 disabled:cursor-wait"
              >
                {isReconnecting ? 'Redirecting…' : 'Reconnect'}
              </button>
              <button
                type="button"
                onClick={() => handleDismiss(acc.id)}
                aria-label={`Dismiss reconnect prompt for ${displayName}`}
                className="text-orange-700 hover:text-orange-900 px-1"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
